/**
 * Single source of truth for the production base-URL default — shared by the
 * runtime SEO meta (`meta.ts` → baked into the HTML at prerender) and the
 * build-time sitemap generator (`scripts/gen-sitemap.mjs`). Keeping the default
 * in ONE place means the G1 prod-domain cutover flips the HTML canonical/hreflang
 * AND the sitemap from a single `VITE_SITE_URL` env — never a code change in two
 * spots that could drift (A5/G1).
 *
 * Pure constant, zero imports/runtime, so `scripts/gen-sitemap.mjs` can import
 * this module directly under `node --experimental-strip-types` — same as it does
 * with `i18n/locales.ts`, and same as `fetch-cms-content.mjs` already does. (The
 * upstream design app instead had `tsc -b` emit a `.js` twin next to this file;
 * that setup did not come across with the port, and this comment used to claim
 * it had.)
 *
 * Default = where the NEW site lives today (Railway) — deliberately NOT
 * `done-it.app`, which still serves the OLD site (A5).
 */
export const DEFAULT_SITE_URL = "https://doneit-design-production.up.railway.app";

/**
 * True only once the G1 prod-domain cutover has happened — i.e. `VITE_SITE_URL` was
 * set to a REAL domain different from the staging default. While false (the Railway
 * staging URL), the site MUST be noindex: it carries machine-translated, not-yet
 * native/legal-reviewed copy, and that must never be indexed on the staging URL.
 * One lever (`VITE_SITE_URL`) flips the domain AND indexability together, so the
 * site goes public exactly when the real domain does — never before.
 */
export const isPublicSite = (siteUrl: string): boolean =>
  siteUrl.replace(/\/$/, "") !== DEFAULT_SITE_URL;
